import type { NotificationKind, WireIncident, WireNotification } from '@/api/wire';
import { distinct, plural } from '@/tasks/explain';
import type { ItemContext, StepAction } from '@/ui/context';
import type { Who } from '@/ui/who';
import { humanise, metricWords } from '@/ui/words';
import { ageOf } from './age';

/**
 * What a notification and an incident say about themselves (ADR-0155).
 *
 * A notification holds ids and a path, and the platform says its sentence on
 * the way out; this adds what kind of thing it is, what is asked of the
 * reader (or, as plainly, that nothing is) and what to do. It does not know
 * more than the row: who caused it is the row's actor, where it leads is the
 * row's own path, and a kind it has no words for is said as the kind.
 */

interface KindSpec {
  /** What kind of event this is, as a noun phrase. */
  what: string;
  /** What it asks of the reader, or null when nothing. */
  need: string | null;
  tip: string;
  instead?: string;
  /** The line the bell menu carries under the sentence. */
  gist: string;
  /** Whether the reader has something to do. */
  needsYou: boolean;
}

const KINDS: Record<NotificationKind, KindSpec> = {
  task_review: { what: 'Tasks waiting for your decision', need: 'Do, schedule, delegate, postpone or drop each task.', tip: 'The original age remains visible. An ignored prompt asks again after three calendar days.', gist: 'Unattended tasks need a decision', needsYou: true },
  build_waiting: {
    what: 'A change waiting for approval',
    need: 'Your approval: read what the change will do and approve it, or send it back.',
    tip: 'Open it and read the read-back of what will change. Approve it if it is what was asked, or return it with a reason.',
    instead: 'Nothing goes live until a lead or a superadmin approves; leaving it only keeps it waiting.',
    gist: 'Needs your approval',
    needsYou: true,
  },
  build_live: {
    what: 'A change that went live',
    need: null,
    tip: 'Nothing is needed from you. The change is live and is watched by its rollback triggers.',
    instead: 'If something looks wrong, say so in the chat; a trigger also takes it down on its own if a measure crosses its line.',
    gist: 'Nothing needed',
    needsYou: false,
  },
  build_returned: {
    what: 'A change sent back to draft',
    need: 'A look at why it came back, and a decision to change the request or let it go.',
    tip: 'Open it and read the reason it was returned. Change what was asked and confirm it again.',
    instead: 'A change gets three tries; the third goes to an engineer, so say clearly what is different this time.',
    gist: 'Came back with a reason',
    needsYou: true,
  },
  request_arrived: {
    what: 'A request from another department',
    need: 'An answer: do what was asked and seal the result, or refuse with a reason.',
    tip: 'Open the request and answer it: do what it asks and seal the result for them, or refuse and say why. A request confers nothing your department does not already hold.',
    instead: 'If you need something from them first, say so on the request rather than leaving it unanswered.',
    gist: 'Needs an answer',
    needsYou: true,
  },
  request_answered: {
    what: 'An answer to a request you made',
    need: null,
    tip: 'Read the result. If it is what you needed, nothing more is asked of you.',
    instead: 'If it falls short, make a new request and say what is missing; the old one stays as it is.',
    gist: 'Read the answer',
    needsYou: false,
  },
  request_refused: {
    what: 'A request that was refused',
    need: 'A decision: reword and ask again, or let it go.',
    tip: 'Open the request and read the reason it was refused. Reword it so it asks for something the other department may do, then ask again.',
    instead: 'If the reason is unclear, ask the other department’s lead before asking again.',
    gist: 'Refused, with a reason',
    needsYou: true,
  },
  incident: {
    what: 'An incident: a rollback trigger fired',
    need: 'Your acknowledgement, once you have read what was measured and what was taken down.',
    tip: 'Open the incident, read what was measured against what, and what was taken down, then acknowledge it. The next breach of the same trigger opens a new one.',
    instead: 'Putting the change back live is a new change through the loop, with its own approval; it is never undone silently.',
    gist: 'Read it and acknowledge',
    needsYou: true,
  },
  publication_drafted: {
    what: 'A draft in your voice',
    need: 'Your decision on the draft: post it as it is, edit it, or discard it.',
    tip: 'Read the draft. Only you decide it; post it yourself where it belongs, edit it, or discard it.',
    instead: 'The platform never posts for you; a draft you leave stays a draft.',
    gist: 'A draft waits for your decision',
    needsYou: true,
  },
  agent_stopped: {
    what: 'An agent stopped by a superadmin',
    need: null,
    tip: 'Nothing is needed from you. The agent does no work until a superadmin starts it again.',
    instead: 'If you need its work, ask a superadmin why it was stopped and when it can start again.',
    gist: 'Nothing needed',
    needsYou: false,
  },
  department_stopped: {
    what: 'A department stopped by a superadmin',
    need: null,
    tip: 'Nothing is needed from you. Its agents do no work until a superadmin starts it again.',
    instead: 'If something urgent is stuck behind it, ask a superadmin directly.',
    gist: 'Nothing needed',
    needsYou: false,
  },
  ledger_proposal: {
    what: 'An edit to a ledger document made in Drive',
    need: 'A decision: accept the edit into the ledger or decline it.',
    tip: 'Open the proposal and compare the edit with the committed text. Accept it only if the change is meant; decline it otherwise.',
    instead: 'Until it is decided, the committed text is what counts.',
    gist: 'An edit waits for your decision',
    needsYou: true,
  },
  connection_rotation_due: {
    what: 'A connection key nearing its rotation date',
    need: 'The key rotated before its term ends.',
    tip: 'Open it and rotate the key before the term runs out. Rotating is planned work, not an emergency.',
    instead: 'If it is left, the connection stops working when the key expires.',
    gist: 'A key is due for rotation',
    needsYou: true,
  },
  vendor_alert: {
    what: 'A vendor’s usage crossed a line, or a plan is ending',
    need: 'A look at what is used, and a decision to raise the limit, change the plan or let it run out.',
    tip: 'Open it and see which vendor and what was used. Decide whether the use is right; if so, raise the ceiling or renew the plan.',
    instead: 'If you do nothing, the tool stops at its limit and what it already found stays in our own database.',
    gist: 'A vendor needs a decision',
    needsYou: true,
  },
  stranger_digest: {
    what: 'The day’s digest of people who wrote in that we do not know',
    need: 'A decision for each: approve them, know them without approving, or dismiss.',
    tip: 'Open the holding pen and decide each sender. Approving files them as a contact; dismissing writes nothing to them.',
    instead: 'Anyone left undecided is shredded after thirty days, unread.',
    gist: 'Strangers wait for a decision',
    needsYou: true,
  },
  campaign_ended: {
    what: 'A campaign that ended',
    need: null,
    tip: 'Nothing is needed from you. Open it to read why it ended: a breach, its window or its ceiling.',
    instead: 'If you want another, start a new campaign; an ended one is never restarted.',
    gist: 'Read why it ended',
    needsYou: false,
  },
  outreach_digest: {
    what: 'Last week in outreach, summarised for the lead',
    need: null,
    tip: 'Nothing is needed from you. Read it to see what moved last week and decide what to focus on.',
    instead: 'Ask the department’s agent about any number you want explained.',
    gist: 'A summary to read',
    needsYou: false,
  },
  message_received: {
    what: 'A message from a colleague',
    need: 'A reply, if one is wanted.',
    tip: 'Open the conversation and reply if the message asks something.',
    instead: 'If it needs no answer, opening it marks it read.',
    gist: 'A colleague wrote to you',
    needsYou: true,
  },
  feature_notice: {
    what: 'A message from a feature the platform built',
    need: null,
    tip: 'Nothing is needed from you unless the message asks for something. Open it to read what the feature says.',
    instead: 'If it is not useful, tell the person who asked for the feature.',
    gist: 'A feature has something to say',
    needsYou: false,
  },
  task_blocked: {
    what: 'A twin’s task waiting on a tool only a superadmin can grant',
    need: 'A grant of the named tools to that one task, or a decision to leave it waiting.',
    tip: 'Open the Workspace, see which tools the task waits on, and grant them to that task if they are right for it.',
    instead: 'A grant is bound to the one task and ends with it. Left alone, the task simply waits.',
    gist: 'A task waits for a grant',
    needsYou: true,
  },
  twin_task_done: {
    what: 'A twin’s task that finished',
    need: null,
    tip: 'Nothing is needed from you. Read the answer and trust a source only where its page was read back.',
    instead: 'If it is not what you needed, ask again in the side chat with what was missing.',
    gist: 'Your twin has an answer',
    needsYou: false,
  },
  twin_task_returned: {
    what: 'A twin’s task handed back to you',
    need: 'The task done by you; the reason is on the task.',
    tip: 'Open the task and read why your twin handed it back, then do it yourself.',
    instead: 'Or ask again in the side chat, saying the blocked part differently.',
    gist: 'Handed back to you',
    needsYou: true,
  },
  suggestion_decided: {
    what: 'The decision on a suggestion you made for a page',
    need: null,
    tip: 'Nothing is needed from you. Read the decision; an accepted suggestion is opened as an issue for engineers, and nothing is built from it automatically.',
    instead: 'If you disagree, make the suggestion again with more detail.',
    gist: 'Your suggestion was decided',
    needsYou: false,
  },
  removal_pattern: {
    what: 'The same kind of item removed from the day again and again',
    need: 'A decision: stop it for good with a rule or a direction, or say no.',
    tip: 'Open it to see where the items come from, then make the rule or direction it offers, or decline it.',
    instead: 'Declined, it is not offered again for that source; you can still remove each item as it comes.',
    gist: 'Offered once: stop it for good',
    needsYou: true,
  },
  project_digest: {
    what: 'Last week in a project you are on, in counts',
    need: null,
    tip: 'Nothing is needed from you. Open the project to see what was decided and what is still open.',
    instead: 'A quiet week sends nothing, so a digest always means something moved.',
    gist: 'A project’s week to read',
    needsYou: false,
  },
  work_stalled: {
    what: 'Something on the platform that is not moving: a request, or background work that keeps failing',
    need: 'A look at where each stands, and the step it names.',
    tip: 'Open Health on the Workspace: each request says who has it, how its work went and what holds it up.',
    instead: 'Ask your twin “why is this stuck?” on the request itself; it reads the same records.',
    gist: 'Something is stuck',
    needsYou: true,
  },
  operation_done: {
    what: 'Your twin read the platform’s cloud project or repository, as you asked',
    need: null,
    tip: 'Open Operations to read the answer; your twin can explain it in the chat.',
    instead: 'Ask your twin in the side chat; it answers from the same reading.',
    gist: 'A reading is ready',
    needsYou: false,
  },
  nightly_audit: {
    what: 'Your twin’s audit of the platform last night',
    need: 'A look at what it found, what it proposed and the briefs it wrote.',
    tip: 'Open Operations: run or refuse each proposal; give a brief to your coding assistant over MCP, or open it as an issue.',
    instead: 'Ask your twin in the side chat what to look at first.',
    gist: 'Last night’s audit',
    needsYou: true,
  },
  operation_waits: {
    what: 'A change to the platform your twin proposed, or a read that could not run',
    need: 'Run it, refuse it, or grant what the read is missing.',
    tip: 'Open Operations: a change says what it does and what it needs; a critical one needs a token minted for it.',
    instead: 'Nothing runs until you decide; leaving it changes nothing.',
    gist: 'A platform change waits on you',
    needsYou: true,
  },
};

const specOf = (kind: string): KindSpec | null => (KINDS as Record<string, KindSpec | undefined>)[kind] ?? null;

/** The line the bell carries under a notification: what to do, or that nothing is needed. */
export function notificationGist(n: Pick<WireNotification, 'kind'>): { line: string; needsYou: boolean } {
  const spec = specOf(n.kind);
  return spec ? { line: spec.gist, needsYou: spec.needsYou } : { line: 'Open it to read more', needsYou: false };
}

/** The sentence the platform said, as the summary, with the kind of event said first. */
export function explainNotification(n: WireNotification, who: Who, opts: { markRead?: (() => void) | undefined } = {}): ItemContext {
  const spec = specOf(n.kind);
  const actor = who.actor(n.actor);
  const dept = n.department ? who.label(n.department) : null;
  const actions: StepAction[] = [];
  if (!n.readAt && opts.markRead) actions.push({ label: 'Mark as read', run: opts.markRead });
  return {
    subject: { kind: 'notification', title: n.text },
    summary: `${spec ? `${spec.what}. ` : `${humanise(n.kind)}. `}${n.text}`,
    ...(spec?.need ? { need: spec.need } : {}),
    why: `It is addressed to you${dept ? ` because it happened in ${dept}` : ', and it concerns the whole company'}. Nobody else sees it.`,
    source: { label: spec?.what ?? humanise(n.kind), to: n.deepLink, when: ageOf(n.createdAt) },
    broughtBy: actor ? { ...actor, when: ageOf(n.createdAt) } : null,
    ...(dept ? { knows: distinct(who.knowers(n.department)).slice(0, 3) } : {}),
    facts: [['Told', ageOf(n.createdAt)], ['Read', n.readAt ? ageOf(n.readAt) : 'Not yet']],
    next: {
      tip: spec?.tip ?? 'Open it to read what happened. Nothing else is known about it here.',
      ...(spec?.instead ? { instead: spec.instead } : {}),
      ...(actions.length > 0 ? { actions } : {}),
    },
    open: { to: n.deepLink, label: 'Open what it is about' },
  };
}

// ---------------------------------------------------------------------------
// Incidents
// ---------------------------------------------------------------------------

const ACTION_WORDS: Record<WireIncident['action'], string> = {
  pause_and_revert: 'paused it and took the change down',
  pause: 'paused it',
  alert: 'only raised the alert',
};

/** An incident as a row of the list or at the top of its page. */
export function explainIncident(i: WireIncident, who: Who, opts: { onPage?: boolean | undefined } = {}): ItemContext {
  const dept = i.departmentLabel;
  const rule = `${metricWords(i.metric)}${i.comparator && i.thresholdValue !== null ? ` ${i.comparator} ${i.thresholdValue}` : ''}`;
  const lead = who.lead(i.department);
  const acknowledged = i.acknowledgedAt !== null;
  const impact =
    i.action === 'alert'
      ? 'Nothing was taken down: this trigger only alerts.'
      : i.reverted.length > 0
        ? `Taken down: ${i.reverted.join(', ')}.`
        : 'Nothing was live to take down.';
  const recurring = i.occurrences > 1;
  const next: ItemContext['next'] = acknowledged
    ? {
        tip: 'Nothing is needed from you. It was acknowledged; the next breach of this trigger opens a new incident.',
        instead: i.action === 'alert' ? 'If this alert keeps coming, ask the department’s lead whether the threshold is right.' : 'Putting the change back live is a new change through the loop, with its own approval.',
      }
    : i.action === 'alert'
      ? {
          tip: `${recurring ? `It has fired ${plural(i.occurrences, 'time')}. ` : ''}Read what was measured and acknowledge it. Nothing was taken down, so decide whether ${metricWords(i.metric).toLowerCase()} really is a problem or the threshold is wrong.`,
          instead: 'If the threshold is wrong, the department’s lead changes it as a change through the loop; acknowledging does not change it.',
        }
      : {
          tip: `${recurring ? `It has fired ${plural(i.occurrences, 'time')} while open. ` : ''}Read what was measured and what was taken down, then acknowledge it so the next breach opens a new incident.`,
          instead: 'Putting the change back live is a new change through the loop, with its own approval: it is never undone silently.',
        };
  return {
    subject: { kind: 'incident', title: `${dept}: ${metricWords(i.metric).toLowerCase()} past its threshold` },
    summary: i.explanation || `${dept}: ${rule}. The platform ${ACTION_WORDS[i.action]}.`,
    need: acknowledged ? undefined : 'Your acknowledgement, once you have read what happened.',
    why: `${dept} agreed this threshold, and a trigger watching it fired without anyone present. You are told because you lead ${dept} or oversee the platform.`,
    source: { label: `The rollback trigger on ${metricWords(i.metric).toLowerCase()} in ${dept}`, when: ageOf(i.firstSeenAt) },
    broughtBy: { name: 'The platform', kind: 'system', role: 'a rollback trigger', when: ageOf(i.firstSeenAt) },
    owner: acknowledged ? { name: i.acknowledgedBy ?? 'Someone', kind: 'person', role: 'acknowledged it' } : lead,
    knows: distinct(who.knowers(i.department)).slice(0, 3),
    facts: [
      ['What fired', rule],
      ['Impact', impact],
      ['Seen', recurring ? `${plural(i.occurrences, 'time')}, last ${ageOf(i.lastSeenAt)}` : `once, ${ageOf(i.lastSeenAt)}`],
      ['Who was told', i.told.length > 0 ? i.told.join(', ') : 'Nobody could be: no lead and no superadmin'],
      ['Their note', i.note],
    ],
    next,
    ...(opts.onPage ? {} : { open: { to: `/incidents/${i.id}`, label: 'Open the incident' } }),
  };
}
